import {
  AmpersandProvider,
  ConnectProvider,
  InstallIntegration
} from "@amp-labs/react";
import "@amp-labs/react/styles";
import "@/styles/ampersand.css";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import { UserData } from "@/entities/users/types";
import { AccountData } from "@/entities/accounts/types";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";
import { cn } from "@/utils/utils";
import { ampersandOptions } from "@/pages/Integrations/ampersandOptions";

interface Props {
  userData: UserData;
  account: AccountData;
  integrationName: string;
  expertMode?: boolean;
  onInstallSuccess?: (installationId: string, configObject: unknown) => void;
  onUpdateSuccess?: (installationId: string, configObject: unknown) => void;
  onUninstallSuccess?: (installationId: string) => void;
  className?: string;
}

const IntegrationsWrapper: React.FC<Props> = ({
  userData,
  account,
  integrationName,
  expertMode = false,
  onInstallSuccess,
  onUpdateSuccess,
  onUninstallSuccess,
  className
}) => {
  const handleError = (error: Error) => {
    toast.error(
      <ToastBody
        title={`Failed to perform operation on ${integrationName}: ${error.message}`}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      { icon: null }
    );
  };

  const handleInstall = (installationId: string, configObject: unknown) => {
    try {
      onInstallSuccess?.(installationId, configObject);
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleUpdate = (installationId: string, configObject: unknown) => {
    try {
      onUpdateSuccess?.(installationId, configObject);
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleUninstall = (installationId: string) => {
    try {
      onUninstallSuccess?.(installationId);
    } catch (error) {
      handleError(error as Error);
    }
  };

  return (
    <div data-test="integrations-page">
      <div
        className={cn("min-h-screen flex items-center justify-center", className)}
      >
        <AmpersandProvider options={ampersandOptions}>
          {expertMode ? (
            <InstallIntegration
              integration={integrationName}
              consumerRef={userData.id}
              consumerName={userData.displayName}
              groupRef={account.id}
              groupName={account.name}
              onInstallSuccess={handleInstall}
              onUpdateSuccess={handleUpdate}
              onUninstallSuccess={handleUninstall}
            />
          ) : (
            <ConnectProvider
              provider={integrationName}
              consumerRef={userData.id}
              consumerName={userData.displayName}
              groupRef={account.id}
              groupName={account.name}
              onConnectSuccess={() => handleInstall("", null)}
              onDisconnectSuccess={() => handleUninstall("")}
            />
          )}
        </AmpersandProvider>
      </div>
    </div>
  );
};

export default IntegrationsWrapper;
